/**
 * `useMe` — fetches `/api/me` via TanStack Query, mirrors the result into
 * the Zustand `useSession` store, and reports auth state to the auth
 * guard.  401 is treated as "anonymous" rather than an error so the
 * `<AuthGuard/>` (PR10.B) can flip the route to /login without a render
 * loop.
 */
import { useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { useEffect } from "react";
import { ApiError } from "@/api/client";
import { getMe, type MeResponse } from "@/api/auth";
import { useSession } from "@/store/session";

export const ME_QUERY_KEY = ["me"] as const;

/**
 * Fetch /me, treating 401 as `null` (anonymous) so callers do not have
 * to disambiguate between "request failed" and "not logged in".
 */
async function fetchMeOrNull(): Promise<MeResponse | null> {
  try {
    return await getMe();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/**
 * Shared TanStack Query options so the /me cache is consistent between
 * the React `useMe` hook and the router's `beforeLoad` guard.
 */
export const meQueryOptions = queryOptions<MeResponse | null, Error>({
  queryKey: ME_QUERY_KEY,
  queryFn: fetchMeOrNull,
  staleTime: 60_000,
  retry: false,
});

export interface UseMeResult {
  user: MeResponse | null;
  isLoading: boolean;
  isAnonymous: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useMe(): UseMeResult {
  const setUser = useSession((s) => s.setUser);
  const query = useQuery(meQueryOptions);

  useEffect(() => {
    if (query.data !== undefined) setUser(query.data);
  }, [query.data, setUser]);

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isAnonymous: query.data === null && !query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/**
 * Force-invalidate `/me` after login/logout/password change so the
 * session state syncs without a hard refresh.
 */
export function useInvalidateMe(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
}
